/**
 * Fixture half of `GET /ops/v1/jobs` (`acs_service/routers/system.py:177`, U-16-f).
 *
 * The store's `list_jobs_filters(device_id, status, action_prefix, offset, limit)` is the thing being
 * mirrored, and three of its behaviours are the reason this module exists rather than an inline
 * `.filter()` in the provider:
 *
 *  1. **`type` is an ACTION PREFIX, not an enum.** The door's own docstring: `diagnostics:` → the
 *     probe family, `activation` → ZTP runs, `apply_policy` → one verb. The action verb lives in
 *     `action` and the diagnostics rows are namespaced with a colon, so `startswith` is the rule and
 *     `===` would silently drop the whole probe family.
 *  2. **`device` and `status` are exact matches** on the stored keys.
 *  3. **`total` counts the rows matching this filter over the whole set**, while `items` is the page,
 *     and `next_cursor` is `null` when the page has consumed `total` — the same pair
 *     `store.list_jobs_filters` returns. A filter that matches nothing is an empty page, never a 404:
 *     an unknown `device` is a query, not a lookup of a resource the caller named.
 *
 * Rows come from `fixtures/records.ts::jobsFor` for every fixture device, so a fleet list is made of
 * the same seeds the per-device panel already shows — one source, no second fleet invented here.
 */
import type { DeviceJobRow, JobListRow, ListJobsQuery, JobsListResponse } from '../api/types';
import { FIXTURE_DEVICES } from './devices';
import { jobsFor } from './records';
import { paginate } from './cursor';

/** A fixture job row plus the device it belongs to — the fleet list's unit. */
export interface FixtureFleetJob {
  readonly device_id: string;
  readonly row: DeviceJobRow;
}

/** The door's row shape: `models.py::JobListItem`, field for field. */
export function jobListItem(deviceId: string, row: DeviceJobRow): JobListRow {
  const item: JobListRow = {
    job_id: row.job_id,
    device_id: deviceId,
    action: row.action,
    status: row.status,
    requested_at: row.requested_at,
    started_at: row.started_at,
    completed_at: row.completed_at,
    result: row.result_summary,
    error: row.error_code ?? null,
  };
  return item;
}

/** Every seeded job of every fixture device, newest first — the store's unfiltered answer. */
export function fixtureFleetJobs(extra: readonly JobListRow[] = []): JobListRow[] {
  const rows: JobListRow[] = [];
  FIXTURE_DEVICES.forEach((device, index) => {
    for (const row of jobsFor(device, index)) rows.push(jobListItem(device.acs_device_id, row));
  });
  return [...extra, ...rows].sort((a, b) => {
    const left = Date.parse(a.requested_at ?? '') || 0;
    const right = Date.parse(b.requested_at ?? '') || 0;
    return right - left;
  });
}

/**
 * The store's filter, spelled the store's way: exact `device`, exact `status`, **prefix** `type`.
 *
 * Exported separately from `fixtureJobsPage` so a test can assert the prefix rule itself: an
 * `activation` filter must keep `activation:*` rows and drop `apply_policy`, and a `diagnostics:`
 * filter must keep the whole probe family.
 */
export function filterJobRows(
  rows: readonly JobListRow[],
  query: Pick<ListJobsQuery, 'device' | 'status' | 'type'>,
): JobListRow[] {
  const device = query.device?.trim();
  const status = query.status?.trim();
  const prefix = query.type?.trim();
  return rows.filter((row) => {
    if (device && row.device_id !== device) return false;
    if (status && row.status !== status) return false;
    if (prefix && !row.action.startsWith(prefix)) return false;
    return true;
  });
}

/** Filter, then page, then count the way `list_jobs_filters` does — `total` is the filtered size. */
export function fixtureJobsPage(
  rows: readonly JobListRow[],
  query: ListJobsQuery,
): JobsListResponse {
  const matched = filterJobRows(rows, query);
  const page = paginate(matched, query.cursor, query.limit ?? 50);
  return { items: page.items, total: matched.length, next_cursor: page.next_cursor };
}
